import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Building2, ListTodo, Loader2, LucideIcon, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DateRangeFilter } from '@/components/admin/DateRangeFilter';
import { OrgGroupedTaskList } from '@/components/admin/OrgGroupedTaskList';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminActivityData } from '@/hooks/useAdminActivityData';
import { useTraitSearch } from '@/hooks/useTraitSearch';
import { logDashboardAccess } from '@/services/adminService';
import { DateRange, TaskWithContext } from '@/types/adminActivity';
import { EntryConfigType } from '@/types/entryConfig';
import NotFound from '@/pages/NotFound';
import { taskPath } from '@shared/appUrls';

interface SummaryCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  color: 'emerald' | 'blue';
  animationDelay: string;
  children?: React.ReactNode;
}

const COLOR_CLASSES = {
  emerald: {
    card: 'from-white to-emerald-50/40 border-emerald-200/40',
    orb: 'bg-emerald-400/5',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-700',
    label: 'text-emerald-900',
  },
  blue: {
    card: 'from-white to-blue-50/40 border-blue-200/40',
    orb: 'bg-blue-400/5',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-700',
    label: 'text-blue-900',
  },
} as const;

function SummaryCard({ icon: Icon, label, value, color, animationDelay, children }: SummaryCardProps) {
  const classes = COLOR_CLASSES[color];

  return (
    <div
      className={`group relative p-6 bg-gradient-to-br ${classes.card} rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden`}
      style={{ animationDelay, animation: 'slideInUp 0.6s ease-out' }}
    >
      <div className={`absolute top-0 right-0 w-24 h-24 ${classes.orb} rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500`} />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-2 ${classes.iconBg} rounded-lg`}>
            <Icon className={`h-5 w-5 ${classes.iconText}`} />
          </div>
          <p className={`text-xs font-bold ${classes.label} uppercase tracking-wider`}>{label}</p>
        </div>
        <p className="text-4xl font-bold text-slate-900 tracking-tight">{value}</p>
        {children}
      </div>
    </div>
  );
}

function createDefaultDateRange(daysBack: number = 7): DateRange {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setDate(start.getDate() - (daysBack - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

const TRAIT_TYPE_COLORS: Record<EntryConfigType, string> = {
  number: 'bg-blue-100 text-blue-800',
  text: 'bg-slate-100 text-slate-800',
  category: 'bg-purple-100 text-purple-800',
  photo: 'bg-amber-100 text-amber-800',
  boolean: 'bg-teal-100 text-teal-800',
  percentage: 'bg-indigo-100 text-indigo-800',
  date: 'bg-orange-100 text-orange-800',
  imageMeasurement: 'bg-rose-100 text-rose-800',
  standCountMeasurement: 'bg-emerald-100 text-emerald-800',
  rowPlantedStandCount: 'bg-lime-100 text-lime-800',
  ruler: 'bg-cyan-100 text-cyan-800',
  transcription: 'bg-fuchsia-100 text-fuchsia-800',
  leafAreaIndex: 'bg-green-100 text-green-800',
  timer: 'bg-rose-100 text-rose-800',
};

/**
 * Super Admin Activity Dashboard.
 * Provides a centralized view of all activity across all organizations.
 * Only accessible to users with superAdmin custom claim.
 */
export function AdminDashboard() {
  const { user, isSuperAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>(createDefaultDateRange);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('activity');

  const { data: activityData, isLoading, error } = useAdminActivityData(dateRange, isSuperAdmin);

  // Lazy-load trait search data only when tab is active
  const {
    searchTerm,
    setSearchTerm,
    searchData,
    isLoading: isTraitSearchLoading,
    error: traitSearchError,
    matchCount,
    traitMatchCount,
  } = useTraitSearch(isSuperAdmin && activeTab === 'trait-search');

  // Log dashboard access on mount
  useEffect(() => {
    if (user && isSuperAdmin) {
      logDashboardAccess(user.uid, user.email || '', dateRange, selectedOrgFilter);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredOrgActivities = useMemo(() => {
    if (!activityData) return [];
    if (!selectedOrgFilter) return activityData.organizationActivities;
    return activityData.organizationActivities.filter(org => org.orgId === selectedOrgFilter);
  }, [activityData, selectedOrgFilter]);

  const filteredSummary = useMemo(() => {
    const tasks = filteredOrgActivities.flatMap(org => org.tasks);
    return {
      totalOrganizations: filteredOrgActivities.length,
      totalTasks: tasks.length,
      completedTasks: tasks.filter(t => t.state === 'completed').length,
      inProgressTasks: tasks.filter(t => t.state === 'in-progress').length,
    };
  }, [filteredOrgActivities]);

  // The task's org URL selects its org (App's URL sync), so no switch is needed here.
  const handleViewTask = useCallback((task: TaskWithContext) => {
    navigate(taskPath(task.orgId, task.trialId, task.experimentId, task.taskId));
  }, [navigate]);

  // Early returns must come AFTER all hooks to satisfy Rules of Hooks
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-amber-50/20">
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(16,185,129,0.03),transparent_50%)]" />
          <div className="relative">
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 mt-2 text-lg">
              Real-time insights across all field operations
            </p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/admin-dashboard/annotations")}>Annotations</Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="trait-search">Trait Search</TabsTrigger>
          </TabsList>

          {/* Activity Tab */}
          <TabsContent value="activity">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8 p-5 bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-xl shadow-sm">
              <div className="flex-1">
                <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Date Range</label>
                <DateRangeFilter value={dateRange} onChange={setDateRange} />
              </div>

              {activityData && activityData.organizationActivities.length > 0 && (
                <div className="flex-1">
                  <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Organization</label>
                  <Select value={selectedOrgFilter || 'all'} onValueChange={(val) => setSelectedOrgFilter(val === 'all' ? null : val)}>
                    <SelectTrigger className="w-full bg-white border-slate-300 hover:border-emerald-400 transition-colors">
                      <SelectValue placeholder="All Organizations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Organizations</SelectItem>
                      {activityData.organizationActivities.map(org => (
                        <SelectItem key={org.orgId} value={org.orgId}>
                          {org.orgName} <span className="text-slate-500">({org.taskCount})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-20 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-4" />
                <span className="text-slate-600 font-medium">Loading field activity data...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-20 bg-red-50/80 backdrop-blur-sm rounded-2xl border border-red-200/60">
                <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
                <span className="text-red-700 font-medium">Error loading activity data</span>
                <p className="text-red-600 text-sm mt-2">{error.message}</p>
              </div>
            )}

            {/* Data Display */}
            {!isLoading && !error && activityData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                  <SummaryCard icon={Building2} label="Organizations" value={filteredSummary.totalOrganizations} color="emerald" animationDelay="0ms" />
                  <SummaryCard icon={ListTodo} label="Total Tasks" value={filteredSummary.totalTasks} color="blue" animationDelay="100ms">
                    <p className="text-xs text-slate-600 mt-2 font-medium">
                      <span className="text-emerald-600">{filteredSummary.completedTasks} completed</span>
                      <span className="text-slate-400 mx-1">&middot;</span>
                      <span className="text-blue-600">{filteredSummary.inProgressTasks} active</span>
                    </p>
                  </SummaryCard>
                </div>

                <OrgGroupedTaskList
                  organizationActivities={filteredOrgActivities}
                  onViewTask={handleViewTask}
                  emptyMessage="No activity found in this date range"
                />
              </>
            )}
          </TabsContent>

          {/* Trait Search Tab */}
          <TabsContent value="trait-search">
            {/* Search Input */}
            <div className="mb-6 p-5 bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-xl shadow-sm">
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Search Traits</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by trait name..."
                  className="pl-10 bg-white border-slate-300 hover:border-emerald-400 focus:border-emerald-500 transition-colors"
                />
              </div>
            </div>

            {/* Loading State */}
            {isTraitSearchLoading && (
              <div className="flex flex-col items-center justify-center py-20 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-4" />
                <span className="text-slate-600 font-medium">Loading trait and task data...</span>
              </div>
            )}

            {/* Error State */}
            {traitSearchError && (
              <div className="flex flex-col items-center justify-center py-20 bg-red-50/80 backdrop-blur-sm rounded-2xl border border-red-200/60">
                <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
                <span className="text-red-700 font-medium">Error loading trait search data</span>
                <p className="text-red-600 text-sm mt-2">{traitSearchError.message}</p>
              </div>
            )}

            {/* No search term */}
            {!isTraitSearchLoading && !traitSearchError && !searchTerm.trim() && (
              <div className="text-center py-20 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-2xl">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium text-lg">Enter a trait name to search</p>
                  <p className="text-slate-500 text-sm mt-2">Find all tasks that reference a specific trait across all organizations</p>
                </div>
              </div>
            )}

            {/* Results with matches */}
            {!isTraitSearchLoading && searchData && searchData.matchingTraits.length > 0 && (
              <>
                {/* Summary */}
                <p className="text-sm text-slate-600 mb-4 font-medium">
                  Found <span className="text-emerald-700 font-bold">{traitMatchCount}</span> matching {traitMatchCount === 1 ? 'trait' : 'traits'}, referenced by <span className="text-blue-700 font-bold">{matchCount}</span> {matchCount === 1 ? 'task' : 'tasks'}
                </p>

                {/* Matching Trait Chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {searchData.matchingTraits.map((trait) => (
                    <div
                      key={trait.path}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm shadow-sm"
                    >
                      <span className="font-medium text-slate-900">{trait.name}</span>
                      <span className="text-slate-400">&middot;</span>
                      <span className="text-slate-500 text-xs">{trait.orgName}</span>
                      <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${TRAIT_TYPE_COLORS[trait.type] || 'bg-slate-100 text-slate-700'}`}>
                        {trait.type}
                      </Badge>
                      {trait.isArchived && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-700 border-amber-300">
                          archived
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>

                {/* Task list grouped by org */}
                <OrgGroupedTaskList
                  organizationActivities={searchData.organizationActivities}
                  onViewTask={handleViewTask}
                  emptyMessage="No tasks reference matching traits"
                />
              </>
            )}

            {/* No matching traits */}
            {!isTraitSearchLoading && searchData && searchData.matchingTraits.length === 0 && (
              <div className="text-center py-20 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-2xl">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium text-lg">No traits found</p>
                  <p className="text-slate-500 text-sm mt-2">Try a different search term</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInSlide {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
